import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  ListItem,
  Typography,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { Class, Schedule } from "../../../../../redux/types/types";

interface CancelDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    cancelDate: string;
    cancelEndDate?: string;
    comment: string;
    cancellationType: "time" | "class";
    selectedSchedule: Schedule | null;
    handleClose: () => void;
  }) => void;
  cl: Class | undefined;
  cancellClassError?: string | null;
}

export default function CancelDialog({
  open,
  onClose,
  onSubmit,
  cl,
  cancellClassError,
}: CancelDialogProps) {
  const intl = useIntl();
  const [cancellationType, setCancellationType] = useState<"time" | "class">(
    "time"
  );
  const [cancelDate, setCancelDate] = useState("");
  const [cancelEndDate, setCancelEndDate] = useState("");
  const [comment, setComment] = useState("");
  const [selectedScheduleId, setSelectedScheduleId] = useState("");
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(
    null
  );
  const [, setDatePickerOpen] = useState(false);
  const [, setEndDatePickerOpen] = useState(false);

  const handleClose = () => {
    onClose();
    setCancelDate("");
    setCancelEndDate("");
    setComment("");
    setCancellationType("time");
    setSelectedScheduleId("");
    setSelectedSchedule(null);
  };

  const handleCancelDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setCancelDate(newValue);

    // If cancelDate is cleared, also clear the cancelEndDate
    if (!newValue) {
      setCancelEndDate("");
    }
  };

  const handleSubmit = async () => {
    if (cancellationType === "time" && cancelDate && selectedScheduleId) {
      await onSubmit({
        cancelDate,
        cancelEndDate: cancelEndDate || undefined,
        comment,
        cancellationType,
        selectedSchedule,
        handleClose,
      });
    }

    if (cancellationType === "class" && cancelDate) {
      await onSubmit({
        cancelDate,
        cancelEndDate: cancelEndDate || undefined,
        comment,
        cancellationType,
        selectedSchedule: null,
        handleClose,
      });
    }
  };

  const weekdays = [
    intl.formatMessage({ id: "days.monday" }),
    intl.formatMessage({ id: "days.tuesday" }),
    intl.formatMessage({ id: "days.wednesday" }),
    intl.formatMessage({ id: "days.thursday" }),
    intl.formatMessage({ id: "days.friday" }),
    intl.formatMessage({ id: "days.saturday" }),
    intl.formatMessage({ id: "days.sunday" }),
  ];

  useEffect(() => {
    if (selectedScheduleId) {
      setSelectedSchedule(
        cl?.schedules.find((s) => s.id === +selectedScheduleId) || null
      );
    }
    //eslint-disable-next-line
  }, [selectedScheduleId]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
        <Button
          onClick={() => setCancellationType("time")}
          variant={cancellationType === "time" ? "contained" : "outlined"}
          sx={{ mx: 1 }}
        >
          <FormattedMessage id="admin.dashboard.class.cancelTitleTimeTitle" />
        </Button>
        <Button
          onClick={() => setCancellationType("class")}
          disabled={cl?.status === "InProgressOfCancellation"}
          variant={cancellationType === "class" ? "contained" : "outlined"}
          sx={{ mx: 1 }}
        >
          <FormattedMessage id="admin.dashboard.class.cancelTitleClassTitle" />
        </Button>
      </Box>
      {cancellationType === "time" ? (
        <>
          <DialogTitle>
            <FormattedMessage id="admin.dashboard.class.cancelTitleTime" />
          </DialogTitle>
          <DialogContent>
            <RadioGroup
              value={selectedScheduleId}
              onChange={(e) => setSelectedScheduleId(e.target.value)}
            >
              {weekdays.map((day, index) => {
                const daySchedules = cl?.schedules
                  ?.filter(
                    (s) =>
                      !s.deactivationDate ||
                      new Date(s.deactivationDate) > new Date()
                  )
                  ?.filter((s) => !s.date && s.dayOfWeek === index);
                if (!daySchedules || daySchedules.length === 0) return null;
                return (
                  <div key={index}>
                    <ListItem>{day}</ListItem>
                    {daySchedules.map((s) => (
                      <FormControlLabel
                        key={s.id}
                        value={s.id}
                        control={<Radio />}
                        label={`${s.startTime} - ${s.endTime}`}
                      />
                    ))}
                  </div>
                );
              })}
            </RadioGroup>
            <TextField
              margin="dense"
              id="cancelDate"
              label={intl.formatMessage({
                id: "admin.dashboard.class.cancelDate",
              })}
              type="date"
              fullWidth
              variant="outlined"
              value={cancelDate}
              onChange={handleCancelDateChange}
              onClick={() => setDatePickerOpen(true)}
              onBlur={() => setDatePickerOpen(false)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: new Date().toISOString().split("T")[0],
                max: cl?.cancelDate
                  ? new Date(cl.cancelDate).toISOString().split("T")[0]
                  : cancelEndDate
                  ? new Date(cancelEndDate).toISOString().split("T")[0]
                  : undefined,
              }}
              sx={{ mt: 2 }}
            />
            <TextField
              margin="dense"
              id="cancelEndDate"
              label={intl.formatMessage({
                id: "admin.dashboard.class.cancelEndDate",
              })}
              type="date"
              fullWidth
              variant="outlined"
              value={cancelEndDate}
              onChange={(e) => setCancelEndDate(e.target.value)}
              onClick={() => setEndDatePickerOpen(true)}
              onBlur={() => setEndDatePickerOpen(false)}
              InputLabelProps={{ shrink: true }}
              disabled={!cancelDate}
              inputProps={{
                min: cancelDate || new Date().toISOString().split("T")[0],
                max: cl?.cancelDate
                  ? new Date(cl.cancelDate).toISOString().split("T")[0]
                  : undefined,
              }}
            />
            <TextField
              margin="dense"
              id="comment"
              label={intl.formatMessage({
                id: "admin.dashboard.class.comment",
              })}
              type="text"
              fullWidth
              variant="outlined"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 1 }}
            />
            {cancellClassError && (
              <Typography color="error">{cancellClassError}</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              disabled={!cancelDate || !selectedScheduleId}
            >
              <FormattedMessage id="admin.dashboard.class.submitCancellation" />
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>
            <FormattedMessage id="admin.dashboard.class.cancelTitle" />
          </DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              id="cancelDate"
              label={intl.formatMessage({
                id: "admin.dashboard.class.cancelDate",
              })}
              type="date"
              fullWidth
              variant="outlined"
              value={cancelDate}
              onChange={handleCancelDateChange}
              onClick={() => setDatePickerOpen(true)}
              onBlur={() => setDatePickerOpen(false)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: new Date(new Date().setDate(new Date().getDate() + 1))
                  .toISOString()
                  .split("T")[0],
              }}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              disabled={!cancelDate}
            >
              <FormattedMessage id="admin.dashboard.class.submitCancellation" />
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
